import { useState } from "react";
import axios from "axios";
import { Building2, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Enterprise (Tier 4) inquiry modal. Collects org info and message,
 * submits to /api/enterprise/inquiries which stores + emails the founder.
 */
export default function EnterpriseInquiryModal({ open, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    organization_name: "",
    contact_name: "",
    email: "",
    phone: "",
    locations_count: "",
    message: "",
  });

  if (!open) return null;

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["organization_name", "contact_name", "email", "phone", "message"];
    for (const k of required) {
      if (!form[k].trim()) {
        toast.error(`Please fill in ${k.replace("_", " ")}.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/enterprise/inquiries`, form);
      toast.success(data.message || "Thanks! We'll be in touch.");
      onClose();
      setForm({ organization_name: "", contact_name: "", email: "", phone: "", locations_count: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="enterprise-inquiry-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-primary">Enterprise Inquiry</h2>
              <p className="text-xs font-body text-text-secondary">Tell us about your organization</p>
            </div>
          </div>
          <button
            data-testid="enterprise-modal-close"
            onClick={onClose}
            type="button"
            className="text-text-secondary hover:text-text-primary p-1 rounded-full hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm font-body text-text-secondary leading-relaxed">
            For organizations with multiple locations or service lines. A member of the Tyvena team
            will reach out within 2 business days to design a tailor-made experience for you.
          </p>

          <div>
            <Label htmlFor="org" className="font-body font-semibold text-text-primary">Organization *</Label>
            <Input id="org" data-testid="enterprise-org" value={form.organization_name}
              onChange={(e) => setField("organization_name", e.target.value)} className="mt-1 h-11 font-body" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="contact" className="font-body font-semibold text-text-primary">Your Name *</Label>
              <Input id="contact" data-testid="enterprise-contact" value={form.contact_name}
                onChange={(e) => setField("contact_name", e.target.value)} className="mt-1 h-11 font-body" />
            </div>
            <div>
              <Label htmlFor="ent-phone" className="font-body font-semibold text-text-primary">Phone *</Label>
              <Input id="ent-phone" type="tel" data-testid="enterprise-phone" value={form.phone}
                onChange={(e) => setField("phone", e.target.value)} className="mt-1 h-11 font-body" />
            </div>
          </div>

          <div>
            <Label htmlFor="ent-email" className="font-body font-semibold text-text-primary">Work Email *</Label>
            <Input id="ent-email" type="email" data-testid="enterprise-email" value={form.email}
              onChange={(e) => setField("email", e.target.value)} className="mt-1 h-11 font-body" />
          </div>

          <div>
            <Label htmlFor="locations" className="font-body font-semibold text-text-primary">Number of locations or service lines</Label>
            <Input id="locations" data-testid="enterprise-locations" value={form.locations_count}
              onChange={(e) => setField("locations_count", e.target.value)} className="mt-1 h-11 font-body"
              placeholder="e.g. 5, 20+, or unsure" />
          </div>

          <div>
            <Label htmlFor="ent-msg" className="font-body font-semibold text-text-primary">What are you hoping to build with Tyvena? *</Label>
            <Textarea id="ent-msg" data-testid="enterprise-message" rows={4} value={form.message}
              onChange={(e) => setField("message", e.target.value)} className="mt-1 font-body" />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}
              className="flex-1 h-11 rounded-full border-2 border-primary text-primary hover:bg-primary/10 font-body font-semibold">
              Cancel
            </Button>
            <Button type="submit" data-testid="enterprise-submit" disabled={submitting}
              className="flex-1 h-11 rounded-full bg-primary text-white hover:bg-[#238C82] font-body font-bold">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Send Inquiry"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
